// =====================================================================
// MisExpenseStrips.jsx
// =====================================================================
// All "intel strip" + overlay components for the MIS Expense Tracker view.
// Previously inlined in `MisCategoryExpenses.jsx` (≈600 lines of UI). Pulled
// into a separate module so the main page can focus on data flow + table.
//
// Each strip is a pure functional component — receives a `summary` (and
// optional `companyTotal`, `monthLabel`, `category`) prop and renders.
// =====================================================================

import { useMemo } from "react";
import { Users, Layers, Wallet, Zap } from "lucide-react";

import { MEMBER_COLOR } from "./PropertyMembersDialog";
import { fmtINR, fmtL, DEPT_COLOR, SALARY_DEPT_ORDER } from "./_format";

// Generic 4-KPI card used by Salary + Members strips (and the main page header).
function KPI({ label, value, sub, accent }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-2.5">
      <div className="text-[9.5px] uppercase tracking-wider font-bold text-zinc-500">{label}</div>
      <div className="font-display font-black tabular-nums mt-1" style={{ fontSize: 17, lineHeight: 1.4, color: accent || "#0f172a" }}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 tabular-nums">{sub}</div>}
    </div>
  );
}

// =====================================================================
// SalarySummaryStrip — MIS Actual vs Wage Sheet (permanent CTC) reconciliation
// + Department mix grid (HK / FD / Maintenance / Cluster / IBT / DM).
// =====================================================================
export function SalarySummaryStrip({ summary, companyTotal, monthLabel }) {
  const gap = summary.contractual_gap || 0;
  const gapPct = summary.contractual_gap_pct;
  const gapTone = gapPct == null ? "text-zinc-500 border-zinc-200" :
                  gapPct > 20 ? "text-rose-800 border-rose-300" :
                  gapPct > 10 ? "text-amber-800 border-amber-300" :
                  "text-emerald-800 border-emerald-300";
  // Memoize the active-dept filter so re-renders skip the .filter pass.
  const activeDepts = useMemo(
    () => SALARY_DEPT_ORDER.filter((d) => (summary.dept_totals || {})[d]),
    [summary.dept_totals]
  );
  return (
    <section className="bg-gradient-to-br from-rose-50 to-indigo-50 border border-indigo-200 rounded-xl p-5" data-testid="mis-cat-salary-strip">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-700 flex items-center gap-1.5">
            <Users className="w-3 h-3" /> Salary intel · MIS Actual vs Wage Sheet (permanent CTC)
          </div>
          <div className="text-[11.5px] text-zinc-600 mt-0.5">
            How much of {monthLabel} salary went to <span className="font-bold">permanent payroll</span> vs <span className="font-bold">contractual / retainers / temporary</span>?
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KPI label="MIS Actual" value={fmtL(companyTotal)} sub={fmtINR(companyTotal)} accent="#FB7185" />
        <KPI label="Permanent CTC (Wage Sheet)" value={fmtL(summary.wage_ctc_total)} sub={`${summary.wage_headcount} employees`} accent="#6366F1" />
        <div className={`bg-white border-2 rounded-md p-2.5 ${gapTone}`}>
          <div className="text-[9.5px] uppercase font-bold tracking-wider opacity-80">Contractual / extra</div>
          <div className="font-display font-black tabular-nums mt-1" style={{ fontSize: 17, lineHeight: 1.4, paddingBottom: 2 }}>
            {fmtL(gap)}
          </div>
          <div className="text-[10px] tabular-nums opacity-80">{gapPct == null ? "—" : `${gapPct}% of MIS`}</div>
        </div>
        <KPI label="Total Headcount" value={summary.wage_headcount ? Math.round(summary.wage_headcount).toString() : "—"} sub="permanent only" accent="#20B2AA" />
        <KPI label="Avg ₹ per Head" value={summary.per_head ? fmtINR(summary.per_head) : "—"} sub="MIS Actual ÷ HC" accent="#FFA500" />
      </div>
      {/* Department mix */}
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">Department mix · {monthLabel}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          {activeDepts.map((d) => {
            const misV = (summary.dept_totals || {})[d] || 0;
            const wageEntry = (summary.wage_by_dept || {})[d] || (summary.wage_by_dept || {})[d.replace(" - ", " ")] || {};
            const wHC = wageEntry.headcount || 0;
            const wCTC = wageEntry.ctc || 0;
            const deptGap = misV - wCTC;
            const deptGapPct = misV > 0 ? Math.round(deptGap / misV * 100) : null;
            return (
              <div key={d} className="bg-white rounded-md border border-zinc-200 p-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: DEPT_COLOR[d] || "#94a3b8" }} />
                  <span className="font-bold text-[11.5px] text-zinc-800">{d}</span>
                </div>
                <div className="tabular-nums text-[11px] text-zinc-700">MIS: <span className="font-bold">{fmtL(misV)}</span></div>
                <div className="tabular-nums text-[10.5px] text-zinc-600">CTC: {fmtL(wCTC)} · {wHC ? Math.round(wHC) : 0} ppl</div>
                {deptGapPct != null && (
                  <div className={`tabular-nums text-[10px] font-bold mt-0.5 ${deptGapPct > 20 ? "text-rose-700" : deptGapPct > 5 ? "text-amber-700" : "text-emerald-700"}`}>
                    Gap {deptGapPct > 0 ? "+" : ""}{deptGapPct}%
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Small inline horizontal bar showing line-item composition (used by table rows).
export function MemberMixBar({ members, total, order = [] }) {
  if (!members || !total) return <span className="text-[10px] text-zinc-400">—</span>;
  const segs = order.map((m) => [m, members[m] || 0]).filter(([, v]) => v > 0);
  if (segs.length === 0) return <span className="text-[10px] text-zinc-400">—</span>;
  const top2 = [...segs].sort((a, b) => b[1] - a[1]).slice(0, 2);
  return (
    <div>
      <div className="flex h-2 rounded overflow-hidden bg-zinc-100">
        {segs.map(([m, v]) => (
          <div key={m}
               title={`${m}: ₹${(v / 100000).toFixed(1)}L (${Math.round(v / total * 100)}%)`}
               style={{ flexBasis: `${v / total * 100}%`, background: MEMBER_COLOR[m] || "#94a3b8" }} />
        ))}
      </div>
      <div className="text-[9.5px] text-zinc-500 mt-1 tabular-nums overflow-hidden text-ellipsis whitespace-nowrap">
        {top2.map(([m, v]) => `${m.replace(" - ", "-").split(" ")[0]} ${Math.round(v / total * 100)}%`).join(" · ")}
      </div>
    </div>
  );
}

// =====================================================================
// MembersSummaryStrip — Composition of a multi-member operational group
// (Utility / R&M / HK & Laundry / Others). Lists every member with its
// share of the category total + a VAR/FIX chip.
// =====================================================================
export function MembersSummaryStrip({ summary, companyTotal, category, monthLabel }) {
  const ordered = summary.ordered_members || [];
  const top = summary.top_member;
  return (
    <section className="bg-gradient-to-br from-teal-50 to-indigo-50 border border-indigo-200 rounded-xl p-5" data-testid="mis-cat-members-strip">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-700 flex items-center gap-1.5">
            <Layers className="w-3 h-3" /> {category} intel · line-item composition
          </div>
          <div className="text-[11.5px] text-zinc-600 mt-0.5">
            Which sub-head drives {monthLabel} {category.toLowerCase()} spend? AOP plans against the rolled-up {category.toLowerCase()} number only.
          </div>
        </div>
        {top && (
          <div className="text-right">
            <div className="text-[9.5px] uppercase tracking-wider font-bold text-zinc-500">Largest line item</div>
            <div className="font-display font-black text-zinc-900 text-[16px]" style={{ lineHeight: 1.4, paddingBottom: 2 }}>{top}</div>
            <div className="text-[10.5px] text-zinc-600 tabular-nums">{summary.top_member_share_pct}% of {category}</div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {ordered.map((mem) => {
          const v = (summary.member_totals || {})[mem] || 0;
          const share = companyTotal ? Math.round(v / companyTotal * 100) : 0;
          const isVar = (summary.is_variable_map || {})[mem];
          return (
            <div key={mem} className="bg-white rounded-md border border-zinc-200 p-2.5" data-testid={`mis-cat-member-${mem.replace(/\W/g, "")}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: MEMBER_COLOR[mem] || "#94a3b8" }} />
                <span className="font-bold text-[11.5px] text-zinc-800">{mem}</span>
                <span className={`text-[8.5px] uppercase tracking-wider font-bold px-1 py-0.5 rounded ${isVar ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-zinc-50 text-zinc-600 border border-zinc-200"}`}>
                  {isVar ? "VAR" : "FIX"}
                </span>
              </div>
              <div className="tabular-nums text-[14px] text-zinc-900 font-display font-black" style={{ lineHeight: 1.4, paddingBottom: 2 }}>{fmtL(v)}</div>
              <div className="tabular-nums text-[10.5px] text-zinc-600">{fmtINR(v)} · {share}% of {category}</div>
              <div className="mt-1 h-1 rounded-full bg-zinc-100 overflow-hidden">
                <div className="h-full" style={{ width: `${share}%`, background: MEMBER_COLOR[mem] || "#94a3b8" }} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// =====================================================================
// OccupancyOverlay — Dashed line connecting weighted-occupancy points across the trend.
// Designed to be rendered INSIDE the bar-zone grid container (which is `position: relative,
// items-end, height: 76`). The overlay sits at bottom: 0 spanning the bar area exactly.
// Bar value labels and YoY chips live in SEPARATE grid rows above/below — no collisions.
// =====================================================================
export function OccupancyOverlay({ trend }) {
  if (!trend?.length) return null;
  const pts = trend.map((t) => t.occ_pct);
  const hasOcc = pts.some((p) => p != null);
  if (!hasOcc) return null;
  const barAreaHeight = 72;
  const n = trend.length;
  const xPct = (i) => ((i + 0.5) / n) * 100;
  const yPct = (occ) => 100 - Math.min(100, Math.max(0, occ));
  const linePoints = trend
    .map((t, i) => (t.occ_pct == null ? null : `${xPct(i)},${yPct(t.occ_pct)}`))
    .filter(Boolean)
    .join(" ");
  return (
    <div className="absolute pointer-events-none" style={{ left: 0, right: 0, bottom: 0, height: barAreaHeight }} data-testid="mis-cat-occ-overlay">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        <line x1="0" y1="50" x2="100" y2="50" stroke="#312e81" strokeOpacity="0.08" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
        <line x1="0" y1="25" x2="100" y2="25" stroke="#312e81" strokeOpacity="0.06" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
        <line x1="0" y1="75" x2="100" y2="75" stroke="#312e81" strokeOpacity="0.06" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
        <polyline
          points={linePoints}
          fill="none"
          stroke="#0f766e"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {trend.map((t, i) =>
        t.occ_pct == null ? null : (
          <div
            key={t.key}
            data-testid={`mis-cat-occ-dot-${i}`}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
            style={{ left: `${xPct(i)}%`, top: `${yPct(t.occ_pct)}%` }}
            title={`${t.month} occupancy: ${t.occ_pct}%`}
          >
            <span className="w-2 h-2 rounded-full bg-teal-700 ring-2 ring-white shadow-sm" />
            <span className="text-[8.5px] tabular-nums font-bold text-teal-800 bg-white/85 backdrop-blur-sm rounded px-0.5 mt-0.5 leading-tight">{t.occ_pct}%</span>
          </div>
        )
      )}
    </div>
  );
}

// =====================================================================
// FinanceCostOverlay — Line connects each month's "FC % of GST base" + a shaded benchmark band (15-18%).
// Renders inside the bar-zone (bottom: 0, height: 72) so it perfectly aligns with the bars.
// =====================================================================
export function FinanceCostOverlay({ trend, summary }) {
  if (!trend?.length) return null;
  const hasPct = trend.some((t) => t.pct_of_base != null);
  if (!hasPct) return null;
  const barAreaHeight = 72;
  const n = trend.length;
  const lo = summary?.benchmark_low_pct ?? 15;
  const hi = summary?.benchmark_high_pct ?? 18;
  // Auto-scale Y axis: domain 0 to max(20, maxPct + 4) so the band always shows even when current numbers are low.
  const maxPct = Math.max(20, ...trend.map((t) => t.pct_of_base ?? 0)) + 2;
  const xPct = (i) => ((i + 0.5) / n) * 100;
  const yPct = (p) => 100 - (Math.min(maxPct, Math.max(0, p)) / maxPct) * 100;
  const linePoints = trend
    .map((t, i) => (t.pct_of_base == null ? null : `${xPct(i)},${yPct(t.pct_of_base)}`))
    .filter(Boolean)
    .join(" ");
  const bandTop = yPct(hi);
  const bandBottom = yPct(lo);
  return (
    <div className="absolute pointer-events-none" style={{ left: 0, right: 0, bottom: 0, height: barAreaHeight }} data-testid="mis-cat-fc-overlay">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        {/* Benchmark band 15-18% */}
        <rect x="0" y={bandTop} width="100" height={bandBottom - bandTop} fill="#10b981" fillOpacity="0.12" />
        <line x1="0" y1={bandTop} x2="100" y2={bandTop} stroke="#059669" strokeOpacity="0.5" strokeWidth="0.5" strokeDasharray="2,2" vectorEffect="non-scaling-stroke" />
        <line x1="0" y1={bandBottom} x2="100" y2={bandBottom} stroke="#059669" strokeOpacity="0.5" strokeWidth="0.5" strokeDasharray="2,2" vectorEffect="non-scaling-stroke" />
        <polyline
          points={linePoints}
          fill="none"
          stroke="#7c3aed"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {trend.map((t, i) =>
        t.pct_of_base == null ? null : (
          <div
            key={t.key}
            data-testid={`mis-cat-fc-dot-${i}`}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
            style={{ left: `${xPct(i)}%`, top: `${yPct(t.pct_of_base)}%` }}
            title={`${t.month} · FC ${fmtL(t.value)} / Base ${fmtL(t.gst_base || 0)} = ${t.pct_of_base}%`}
          >
            <span className="w-2 h-2 rounded-full bg-violet-700 ring-2 ring-white shadow-sm" />
            <span className="text-[8.5px] tabular-nums font-bold text-violet-800 bg-white/85 backdrop-blur-sm rounded px-0.5 mt-0.5 leading-tight">{t.pct_of_base}%</span>
          </div>
        )
      )}
      <div className="absolute right-0 top-0 text-[8.5px] uppercase tracking-wider font-bold text-violet-800 bg-white/70 px-1 rounded-bl">
        ITC loss %
      </div>
      <div className="absolute right-0 text-[8.5px] uppercase tracking-wider font-bold text-emerald-700 bg-white/70 px-1 rounded" style={{ top: `${bandTop}%`, transform: "translateY(-100%)" }}>
        {hi}% benchmark
      </div>
    </div>
  );
}

// =====================================================================
// FinanceCostIntelStrip — Pre-Sep 2025 vs Post-Sep 2025 ITC-loss pivot summary.
// =====================================================================
export function FinanceCostIntelStrip({ summary, monthLabel }) {
  const pre = summary.pre_cutover || {};
  const post = summary.post_cutover || {};
  const latest = summary.latest_pct_of_base;
  const lo = summary.benchmark_low_pct;
  const hi = summary.benchmark_high_pct;
  const latestColorClass = latest == null ? "text-zinc-700" :
    latest > (hi + 3) ? "text-rose-800" :
    latest >= lo ? "text-emerald-800" :
    latest >= lo - 5 ? "text-amber-800" :
    "text-zinc-700";
  return (
    <section className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-xl p-5" data-testid="mis-cat-fc-strip">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700 flex items-center gap-1.5">
            <Wallet className="w-3 h-3" /> Finance Cost intel · Ineligible ITC loss
          </div>
          <div className="text-[11.5px] text-zinc-600 mt-0.5">
            Finance Cost is the ITC that the government disallowed from {summary.itc_cutover_label} onwards. Denominator = Total Expense − Salary − Utility (the GST-applicable expense base). Healthy band is {lo}-{hi}% if all eligible credit gets denied at the full 18% rate.
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9.5px] uppercase tracking-wider font-bold text-zinc-500">Latest ({monthLabel})</div>
          <div className={`font-display font-black text-[20px] tabular-nums ${latestColorClass}`} style={{ lineHeight: 1.4 }}>
            {latest == null ? "—" : `${latest}%`}
          </div>
          <div className="text-[10.5px] text-zinc-600 tabular-nums">
            of base · vs {lo}-{hi}% target
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-white border border-zinc-200 rounded-md p-2.5">
          <div className="text-[9.5px] uppercase tracking-wider font-bold text-zinc-500">Pre {summary.itc_cutover_label} · ITC allowed</div>
          <div className="font-display font-black tabular-nums text-zinc-700 mt-1" style={{ fontSize: 17, lineHeight: 1.4 }}>{pre.fc_pct_of_base != null ? `${pre.fc_pct_of_base}%` : "—"}</div>
          <div className="text-[10px] text-zinc-500 tabular-nums">{fmtL(pre.fc_total)} / {fmtL(pre.base_total)} · {pre.months} mo</div>
        </div>
        <div className="bg-white border-2 border-violet-300 rounded-md p-2.5">
          <div className="text-[9.5px] uppercase tracking-wider font-bold text-violet-700">Post {summary.itc_cutover_label} · ITC disallowed</div>
          <div className="font-display font-black tabular-nums text-violet-800 mt-1" style={{ fontSize: 17, lineHeight: 1.4 }}>{post.fc_pct_of_base != null ? `${post.fc_pct_of_base}%` : "—"}</div>
          <div className="text-[10px] text-zinc-500 tabular-nums">{fmtL(post.fc_total)} / {fmtL(post.base_total)} · {post.months} mo</div>
        </div>
        <div className="bg-white border border-emerald-200 rounded-md p-2.5">
          <div className="text-[9.5px] uppercase tracking-wider font-bold text-emerald-700">In {lo}-{hi}% band</div>
          <div className="font-display font-black tabular-nums text-emerald-800 mt-1" style={{ fontSize: 17, lineHeight: 1.4 }}>{summary.within_band_months} mo</div>
          <div className="text-[10px] text-zinc-500">healthy · expected steady-state</div>
        </div>
        <div className="bg-white border border-rose-200 rounded-md p-2.5">
          <div className="text-[9.5px] uppercase tracking-wider font-bold text-rose-700">Above {hi}%</div>
          <div className="font-display font-black tabular-nums text-rose-800 mt-1" style={{ fontSize: 17, lineHeight: 1.4 }}>{summary.above_band_months} mo</div>
          <div className="text-[10px] text-zinc-500">overshoot · investigate vendor mix</div>
        </div>
      </div>
    </section>
  );
}

// =====================================================================
// CommBenchmarkStrip — (Others group only). Expected Communication spend
// = (capacity beds × days_in_month × ₹6.5/bed/day). Tracking began Oct/Nov
// 2025, so we only display the LATEST-MONTH variance — aggregating across
// 12 months would pull in pre-tracking zeros and report a misleading −100%.
// =====================================================================
export function CommBenchmarkStrip({ summary, monthLabel }) {
  const tolerance = summary.tolerance_pct ?? 15;
  const latestVar = summary.latest_variance_pct;
  const overCount = summary.over_threshold_count || 0;
  const tone = (pct) =>
    pct == null ? "text-zinc-700" :
    pct > tolerance ? "text-rose-800" :
    pct > 5 ? "text-amber-800" :
    pct < -tolerance ? "text-zinc-500" :
    "text-emerald-800";
  const latestTone = tone(latestVar);
  return (
    <section className="bg-gradient-to-br from-amber-50 to-rose-50 border border-amber-200 rounded-xl p-5" data-testid="mis-cat-comm-strip">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 flex items-center gap-1.5">
            <Zap className="w-3 h-3" /> Communication cost control · benchmark
          </div>
          <div className="text-[11.5px] text-zinc-600 mt-0.5">
            Expected Communication spend = capacity beds × days × <span className="font-bold tabular-nums">₹{summary.per_bed_per_day_inr}/bed/day</span>. Flag any property &gt; <span className="font-bold tabular-nums">+{tolerance}%</span> over the benchmark for vendor / data-plan review.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-white border border-zinc-200 rounded-md p-2.5">
          <div className="text-[9.5px] uppercase tracking-wider font-bold text-zinc-500">{monthLabel} · Actual</div>
          <div className="font-display font-black tabular-nums text-zinc-800 mt-1" style={{ fontSize: 17, lineHeight: 1.4 }}>{fmtL(summary.latest_actual)}</div>
          <div className="text-[10px] text-zinc-500">company Communication spend</div>
        </div>
        <div className="bg-white border-2 border-amber-300 rounded-md p-2.5">
          <div className="text-[9.5px] uppercase tracking-wider font-bold text-amber-700">{monthLabel} · Benchmark</div>
          <div className="font-display font-black tabular-nums text-amber-800 mt-1" style={{ fontSize: 17, lineHeight: 1.4 }}>{fmtL(summary.latest_benchmark)}</div>
          <div className="text-[10px] text-zinc-500 tabular-nums">@ ₹{summary.per_bed_per_day_inr}/bed/day</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-md p-2.5">
          <div className="text-[9.5px] uppercase tracking-wider font-bold text-zinc-500">{monthLabel} · Variance</div>
          <div className={`font-display font-black tabular-nums mt-1 ${latestTone}`} style={{ fontSize: 17, lineHeight: 1.4 }}>
            {latestVar == null ? "—" : `${latestVar > 0 ? "+" : ""}${latestVar}%`}
          </div>
          <div className="text-[10px] text-zinc-500">
            {latestVar == null ? "—" : (latestVar > tolerance ? "overspend · review" : latestVar < -tolerance ? "underspend / not tracked" : "within tolerance band")}
          </div>
        </div>
        <div className={`bg-white rounded-md p-2.5 border ${overCount > 0 ? "border-rose-300" : "border-emerald-200"}`}>
          <div className={`text-[9.5px] uppercase tracking-wider font-bold ${overCount > 0 ? "text-rose-700" : "text-emerald-700"}`}>Over benchmark</div>
          <div className={`font-display font-black tabular-nums mt-1 ${overCount > 0 ? "text-rose-800" : "text-emerald-800"}`} style={{ fontSize: 17, lineHeight: 1.4 }}>{overCount} props</div>
          <div className="text-[10px] text-zinc-500">&gt; +{tolerance}% in {monthLabel}</div>
        </div>
      </div>
    </section>
  );
}

export { KPI };